import * as THREE from 'three';
import { cellAtWorld, edgeLayout, hexToWorld, LEVEL_ROWS, wallLayout, type BattleState, type CliffLayout, type CliffRules, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The flank's cliff (docs/sendas/explorador.md, chapter 2, «El Acantilado del Flanco»), for the ranger's path. Its own
 * places; the AsterLayer (engine/aster.ts) hands them here.
 *
 * - `cliffTop` (act 1, the cornice): a wide ledge at the top of the flank in the sea wind. Behind it the wall goes up in
 *   strata of black wet rock, the crows' nests stuck in its cracks like soot stains and the deep cracks of the
 *   shriekers; in front, the lip and the drop: hundreds of metres down to the foam. Sela on her knees at the lip
 *   driving the anchor (the iron sinks blow by blow); a hundred paces off, the six red ropes of Galo's crew going down.
 * - `cliffWall` (act 2) and `cliffNight` (act 3): the wall seen from the front. The board's ground is lifted into it:
 *   each level's ledges are shelves cut in the rock, the faces between them steep and falling toward the camera, the
 *   sea at the foot. The cracks on the faces with their lips (untried grey; firm: an iron peg and the rope; hollow:
 *   chipped pale, the arrow fallen; crumbling: split plates, dust while weighed). The rope's reach drawn as a red arc on
 *   the rock round the hero, the cracks in it lit. Dusk (the red sun on the sea, the wall lit from the side) or night
 *   (the moon, the crew's lanterns, the shriekers' cracks black). The shriekers (leather wings, no eyes) and their
 *   mother drawn here; the sounds as rings: a deep slow one for a firm crack, a small quick one for a hollow one, red
 *   rings from a hollow stone, the mother's red ring growing.
 * - The scenes: `flankSea` (the wall from the sea), `asterLedge` (Aster's ledge: a small cave, a ring of stones, the
 *   page stuck with resin), `cliffGrab` (the last ledge at night: the anchor out, a red rope from the dark), `cliffFoot`
 *   (the beach at the foot: a fire, the crew).
 *
 * The board's ground is the wall: its heights are rewritten (`apply`) so the hero, Sela and the crows stand and fly at
 * their shelf. Draw calls: the wall (one mesh), the dressing (one), the sea, the ropes (one tube mesh, rebuilt when a
 * rope is left), the reach arc (one line strip), the shriekers (three instanced meshes), the mother, the rings (one
 * pool), the glow layer, the sky. No lights of its own: the board's are repainted. Every look is a function of the
 * sim's state; frames only move what is there (docs/RENDIMIENTO.md).
 */

export const GALO_PLACES = new Set(['cliffTop', 'cliffWall', 'cliffNight', 'flankSea', 'asterLedge', 'cliffGrab', 'cliffFoot']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const PALE = new THREE.Color('#e4ebf0');
const DEEP = new THREE.Color('#9fc4e8');
const WARM = new THREE.Color('#ffb48a');
const EMBER = new THREE.Color('#ff7a4a');
/** The rock of the flank: black, wet, in strata. */
const ROCK = ['#2b3036', '#353a41', '#23272c', '#3d434a'];
const SHELF = ['#71777b', '#686e72', '#7a8084'];
const ROPE = '#9aa1a6';
const RED_ROPE = '#b8322a';
/** A shelf's drop under the one above it (world units), the sea's level under the wall. */
const DROP = 7.2;
const SEA_Y = -34;

type Hour = 'day' | 'dusk' | 'night';
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sun: [number, number, number] }> = {
  // The cornice: a grey windy morning, the sea light from below.
  day: { bg: '#8796a2', fog: ['#8e9ba6', 40, 140], hemi: ['#dfe7ee', '#2a2f35', 2.0], key: ['#fff1e6', 2.5], rim: ['#b8cde6', 1.4], sky: ['#5e6f7e', '#b9c4cc', '#fff1e4'], sun: [0.4, 0.45, 0.6] },
  // The wall: the red sun low over the sea, from the side.
  dusk: { bg: '#2a3242', fog: ['#2e3646', 60, 170], hemi: ['#d0daf0', '#1a1e27', 2.8], key: ['#ff7050', 1.5], rim: ['#8aa8f4', 2.2], sky: ['#141a2c', '#5e4c60', '#ff6a3e'], sun: [-0.98, 0.1, 0.12] },
  // The night: the moon, cold; what glows is the crew's lanterns and the sounds.
  night: { bg: '#070a10', fog: ['#0b0f18', 50, 140], hemi: ['#8496c2', '#0c0d12', 1.7], key: ['#b4c6ee', 1.5], rim: ['#7d98ff', 1.4], sky: ['#020409', '#101826', '#a8bae6'], sun: [0.5, 0.5, -0.3] },
};

/** The sea at the foot of the wall: a slow swell, foam in lines, white where it breaks on the rock (north edge). */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, foot: { value: 10 }, tint: { value: new THREE.Color('#152430') } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float foot; uniform vec3 tint; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.15 + vec2(time * 0.04, time * 0.06)) * 0.6 + n(vXZ * 0.4 - vec2(0.0, time * 0.09)) * 0.4;
        float foam = smoothstep(0.72, 0.8, n(vec2(vXZ.x * 1.4 + sw * 3.0, vXZ.y * 0.5 - time * 0.12))) * smoothstep(0.35, 0.6, sw);
        float d = vXZ.y - foot;
        float shore = smoothstep(7.0, 0.0, d) * (0.6 + 0.4 * sin(time * 1.3 + vXZ.x * 0.7));
        vec3 col = mix(tint, tint * 1.9 + vec3(0.03, 0.05, 0.06), sw * 0.6);
        col = mix(col, vec3(0.86, 0.92, 0.96), clamp(max(foam * 0.45, shore * smoothstep(0.3, 0.7, sw)), 0.0, 0.9));
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/**
 * The wall's rock, painted per pixel (the vertex colours on a 0.28 grid read as a blurred purple mass on a phone):
 * - the shelves: pale worn stone, gritty, a bright crisp lip along their edges and a dark line just inside it;
 * - the faces: black-blue strata by height (each band its own shade, its top catching light, a thin pale seam between
 *   some), vertical joints, grit; in deep shadow just under each shelf's lip;
 * - depth: the lower, the darker and the more the sea's cold green-blue, so the foot reads far down. No fog: nothing
 *   near is washed.
 * Crisp edges come from steps on interpolated per-vertex marks (`aMark`), never from the grid's colours.
 */
const wallRock = (m: THREE.MeshStandardMaterial, night: boolean): void => {
  m.vertexColors = false;
  m.flatShading = false;
  m.roughness = 0.8;
  m.metalness = 0.04;
  const u = {
    uRock0: { value: new THREE.Color(night ? '#1c2129' : '#262c34') },
    uRock1: { value: new THREE.Color(night ? '#2b323c' : '#3a414b') },
    uSeam: { value: new THREE.Color(night ? '#6d7884' : '#8b959e') },
    uShelf: { value: new THREE.Color(night ? '#8d98a3' : '#939ea8') },
    uLip: { value: new THREE.Color(night ? '#d2dce6' : '#cfd9e2') },
    uSea: { value: new THREE.Color(night ? '#0a1a22' : '#14303a') },
  };
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 aMark;\nvarying vec3 vRkW;\nvarying vec3 vRkM;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRkW = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvRkM = aMark;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uRock0; uniform vec3 uRock1; uniform vec3 uSeam; uniform vec3 uShelf; uniform vec3 uLip; uniform vec3 uSea;
varying vec3 vRkW;
varying vec3 vRkM;
float rkH(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  float deep = clamp(-vRkW.y / ${(4 * DROP).toFixed(1)}, 0.0, 1.0);
  // The faces: strata by height, each band its own shade, the top of each catching light.
  float hb = vRkW.y * 1.05 + sin(vRkW.x * 0.23) * 0.14;
  float band = floor(hb);
  float f = hb - band;
  float hs = rkH(vec2(band, 3.0));
  vec3 face = mix(uRock0, uRock1, hs);
  face *= 0.82 + 0.34 * smoothstep(0.8, 0.97, f);
  float seam = 1.0 - smoothstep(0.0, 0.045, min(f, 1.0 - f));
  face = mix(face, uSeam, seam * step(0.5, rkH(vec2(band, 7.0))) * 0.7);
  // Vertical joints, offset band by band (blocks of rock), and grit.
  float jf = fract(vRkW.x * 0.75 + hs * 7.0);
  face *= 1.0 - (1.0 - smoothstep(0.0, 0.03, min(jf, 1.0 - jf))) * 0.55;
  face *= 0.88 + 0.22 * rkH(floor(vec2(vRkW.x * 8.0, vRkW.y * 8.0 + vRkW.z * 8.0)));
  // Under a shelf's lip: deep shadow.
  face *= 1.0 - 0.6 * smoothstep(0.0, 0.85, vRkM.z);
  // The sea's depth: darker and colder the lower.
  face = mix(face, uSea, deep * 0.5) * (1.0 - deep * 0.3);
  // The shelves: pale stone, gritty; the lip a crisp bright band, a dark line inside it.
  vec3 shelf = uShelf * (0.93 + 0.09 * rkH(floor(vRkW.xz * 11.0)));
  shelf *= 0.94 + 0.1 * sin(vRkW.x * 2.1 + vRkW.z * 1.3);
  float lip = step(0.66, vRkM.y);
  shelf = mix(shelf, shelf * 0.55, step(0.5, vRkM.y) * (1.0 - lip));
  shelf = mix(shelf, uLip, lip);
  shelf *= 1.0 - deep * 0.22;
  diffuseColor.rgb = mix(face, shelf, step(0.5, vRkM.x));
}`,
      );
  };
  m.customProgramCacheKey = () => `galo-rock-${night ? 1 : 0}`;
};

/** The wall's surface from its layout: the shelves' spans, the face's line, the cornice's rise and drop. */
export interface CliffShape {
  act: 'edge' | 'wall' | 'night';
  shelves: { level: number; x0: number; x1: number; y0: number; y1: number; h: number }[];
}

/** The back row of a level's shelf (sim y). */
const backY = (level: number): number => (LEVEL_ROWS[level] as number) * 866 - 433;

export const cliffShape = (L: CliffLayout, act: 'edge' | 'wall' | 'night'): CliffShape => ({
  act,
  shelves: L.ledges.map((l) => {
    if (act === 'edge') return { level: 0, x0: l.x0, x1: l.x1, y0: -2 * 866 - 500, y1: 2 * 866 + 450, h: 0 };
    const r = LEVEL_ROWS[l.level] as number;
    return { level: l.level, x0: l.x0 - 350, x1: l.x1 + 350, y0: r * 866 - 500, y1: (r + 1) * 866 + 460, h: -l.level * DROP };
  }),
});

/** The rock's height (world) at a sim point off the shelves: the face falling toward the sea; the cornice's wall and drop. */
const faceH = (sh: CliffShape, x: number, y: number): number => {
  if (sh.act === 'edge') {
    const back = -2 * 866 - 500;
    const front = 2 * 866 + 450;
    if (y < back) return Math.min(14, (back - y) * K * 1.7);
    if (y > front) return Math.max(SEA_Y - 4, -(y - front) * K * 2.6 - 0.25);
    return 0;
  }
  void x;
  const h = -(y - backY(0)) * (DROP / (5 * 866));
  return Math.max(SEA_Y - 4, Math.min(16, h));
};

/** The surface's height (world) at a sim point. */
export const cliffH = (sh: CliffShape, x: number, y: number): number => {
  for (const s of sh.shelves) if (x >= s.x0 && x <= s.x1 && y >= s.y0 && y <= s.y1) return s.h;
  return faceH(sh, x, y);
};

/** The height a sim point is drawn at by the rules alone (the HUD's marks): its ledge's level, else the face. */
export const cliffRulesH = (s: BattleState, r: CliffRules, x: number, y: number): number => {
  const c = cellAtWorld(s, x, y);
  for (const l of r.ledges) if (l.cells.includes(c)) return r.act === 'edge' ? 0 : -l.level * DROP;
  if (r.act === 'edge') return faceH({ act: 'edge', shelves: [] }, x, y);
  return faceH({ act: r.act, shelves: [] }, x, y);
};

const hash = (n: number): number => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

interface RingFx {
  x: number;
  y: number;
  z: number;
  r0: number;
  r1: number;
  t0: number;
  life: number;
  color: THREE.Color;
  alpha: number;
}

/** A soot crow's nest or a deep crack, its spot and its own look. */
interface Spot {
  p: THREE.Vector3;
  k: number;
}

export class GaloLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private dome: SkyDome | null = null;
  private now = 0;
  private hour: Hour = 'dusk';
  private sea: THREE.ShaderMaterial | null = null;
  private L: CliffLayout | null = null;
  private shape: CliffShape | null = null;
  private act: 'edge' | 'wall' | 'night' | null = null;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; flick: number }[] = [];
  private cracks: { p: THREE.Vector3; k: number; state: number }[] = [];
  /** The cracks' lips, pegs and fallen arrows: one instanced mesh each (one mesh and material a crack was ~40 draws). */
  private lips: THREE.InstancedMesh | null = null;
  private pegs: THREE.InstancedMesh | null = null;
  private arrows: THREE.InstancedMesh | null = null;
  private nests: Spot[] = [];
  private holes: Spot[] = [];
  private stones: { p: THREE.Vector3 }[] = [];
  private stoneMesh: THREE.InstancedMesh | null = null;
  private stoneShook = false;
  private ropeMesh: THREE.Mesh | null = null;
  private ropeKey = '';
  private ropeMat: THREE.MeshStandardMaterial | null = null;
  private tripRope: THREE.Mesh | null = null;
  private arc: THREE.Line | null = null;
  private arcAt = new THREE.Vector3(1e9, 0, 0);
  private rings: RingFx[] = [];
  private ringPool: THREE.InstancedMesh | null = null;
  private crew: { x: number; top: number; bottom: number; k: number }[] = [];
  /** The crew's walkers: one instanced mesh (cloak, head and lantern in its vertex colours). */
  private crewMesh: THREE.InstancedMesh | null = null;
  private anchor: THREE.Group | null = null;
  private bats: { body: THREE.InstancedMesh; wl: THREE.InstancedMesh; wr: THREE.InstancedMesh } | null = null;
  private mother: { g: THREE.Group; wl: THREE.Object3D; wr: THREE.Object3D } | null = null;
  private motherRing: { t0: number; x: number; z: number; y: number } | null = null;
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private e3 = new THREE.Euler();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `galo-${place}`;
    const kit = new Kit();
    if (place === 'cliffTop') this.buildEdge(kit, s);
    else if (place === 'cliffWall' || place === 'cliffNight') this.buildWall(kit, s, place === 'cliffNight');
    else this.buildScene(kit, place);
    const mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0.05, flatShading: true }));
    const dress = kit.build(mat, 'galo-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.group.add(this.glow.mesh);
  }

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, flick });
  }

  /** A sim point on the wall's surface, lifted `h`. */
  private at(x: number, y: number, h = 0): THREE.Vector3 {
    const g = this.shape ? cliffH(this.shape, x, y) : 0;
    return new THREE.Vector3(x * K, g + h, y * K);
  }

  /* ---------- the wall's surface ---------- */

  /**
   * The rock as one mesh: a grid over the board and past it, its height the wall's surface; painted in strata (bands
   * by height, each its own shade), wet near the foot, salt in streaks down the faces, lichen on the shelves' lips,
   * the shelves' tops worn pale.
   */
  private buildSurface(night: boolean): void {
    const sh = this.shape as CliffShape;
    const xs = 18;
    const z0 = sh.act === 'edge' ? -16 : -15;
    const z1 = sh.act === 'edge' ? 12 : 18;
    const step = 0.28;
    const nx = Math.ceil((xs * 2) / step);
    const nz = Math.ceil((z1 - z0) / step);
    const pos = new Float32Array((nx + 1) * (nz + 1) * 3);
    const col = new Float32Array((nx + 1) * (nz + 1) * 3);
    // Per vertex for the wall's shader: on a shelf (0/1), the shelf's lip (1 at its edge), under a shelf's lip (1 just under).
    const mark = new Float32Array((nx + 1) * (nz + 1) * 3);
    const c = new THREE.Color();
    for (let j = 0; j <= nz; j++)
      for (let i = 0; i <= nx; i++) {
        const wx = -xs + i * step;
        const wz = z0 + j * step;
        const sx = wx / K;
        const sz = wz / K;
        let h = cliffH(sh, sx, sz);
        const shelf = sh.shelves.some((s) => sx >= s.x0 && sx <= s.x1 && sz >= s.y0 && sz <= s.y1);
        // The rock's grain: knobs and ribs on the faces (never on the shelves the people stand on).
        const n1 = Math.sin(wx * 1.7 + wz * 0.6) * Math.cos(wz * 2.3 - wx * 0.4);
        const n2 = Math.sin(wx * 4.1 - wz * 3.3) * 0.5;
        if (!shelf) h += n1 * 0.18 + n2 * 0.06;
        const o = (j * (nx + 1) + i) * 3;
        const on = shelf ? sh.shelves.find((q) => sx >= q.x0 && sx <= q.x1 && sz >= q.y0 && sz <= q.y1) : undefined;
        if (on) {
          mark[o] = 1;
          mark[o + 1] = Math.max(0, 1 - Math.min(on.y1 - sz, sx - on.x0, on.x1 - sx) / 520);
        } else {
          let under = 0;
          for (const q of sh.shelves) if (sx >= q.x0 - 200 && sx <= q.x1 + 200 && sz > q.y1) under = Math.max(under, 1 - (sz - q.y1) / 1500);
          mark[o + 2] = under;
        }
        pos[o] = wx;
        pos[o + 1] = h;
        pos[o + 2] = wz;
        if (shelf) {
          // The shelf's top: worn pale stone, grit; its front lip a shade lighter.
          const s = sh.shelves.find((q) => sx >= q.x0 && sx <= q.x1 && sz >= q.y0 && sz <= q.y1);
          const lip = s ? Math.max(0, 1 - (s.y1 - sz) / 380) : 0;
          c.set(SHELF[Math.floor(hash(i * 7 + j * 13) * 2.99)] as string).multiplyScalar(0.9 + 0.15 * n2 + lip * 0.25);
        } else {
          // Strata: bands by height, each its own shade; salt streaks down; wet and dark near the sea.
          // (Bands by height: the strata run level across the wall; a thin pale seam between some.)
          const hb = (h + 60) * 1.25 + Math.sin(wx * 0.35) * 0.4;
          const band = Math.floor(hb);
          c.set(ROCK[band % 4] as string);
          const seam = 1 - Math.min(1, Math.abs(hb - band - 0.5) * 9);
          if (band % 3 === 0) c.lerp(new THREE.Color('#7b848c'), Math.max(0, seam - 0.75) * 1.6);
          // Salt runs down the rock here and there (a few runs, not a pattern: a regular one read as rain).
          const col0 = Math.floor(wx * 0.9);
          const run = hash(col0 * 7.3) > 0.88 ? Math.max(0, 1 - Math.abs(wx * 0.9 - col0 - 0.5) * 8) * (0.5 + 0.5 * Math.sin(wz * 0.7 + col0)) : 0;
          c.lerp(new THREE.Color('#8e979e'), Math.min(0.3, run * 0.3));
          const wet = Math.max(0, Math.min(1, (SEA_Y + 9 - h) / 8));
          c.multiplyScalar(1 + n1 * 0.12 - wet * 0.35);
          if (night) c.multiplyScalar(0.92);
        }
        col[o] = c.r;
        col[o + 1] = c.g;
        col[o + 2] = c.b;
      }
    const idx: number[] = [];
    for (let j = 0; j < nz; j++)
      for (let i = 0; i < nx; i++) {
        const a = j * (nx + 1) + i;
        idx.push(a, a + nx + 1, a + 1, a + 1, a + nx + 1, a + nx + 2);
      }
    const g = this.track(new THREE.BufferGeometry());
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    const m = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.12 }));
    if (sh.act !== 'edge') {
      g.setAttribute('aMark', new THREE.BufferAttribute(mark, 3));
      wallRock(m, night);
    }
    const mesh = new THREE.Mesh(g, m);
    // (It takes shadows, never casts them: the low sun on a wall shading itself drew stripes all down it.)
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    mesh.name = 'galo-wall';
    this.group.add(mesh);
    // The sea at the foot.
    const sea = this.track(seaMat());
    this.sea = sea;
    const foot = sh.act === 'edge' ? 9.5 : 16;
    (sea.uniforms.foot as THREE.IUniform<number>).value = foot;
    if (night) (sea.uniforms.tint as THREE.IUniform<THREE.Color>).value.set('#0a121a');
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(240, 240).rotateX(-Math.PI / 2)), sea);
    plane.position.set(0, SEA_Y, foot + 110);
    plane.name = 'galo-sea';
    this.group.add(plane);
  }

  /* ---------- act 1: the cornice ---------- */

  private buildEdge(kit: Kit, s: BattleState): void {
    const L = edgeLayout(s.seed);
    this.L = L;
    this.act = 'edge';
    this.shape = cliffShape(L, 'edge');
    this.hour = 'day';
    this.buildSurface(false);
    this.dressShelves(kit, L);
    this.buildNests(kit, L);
    this.buildCrew(L.crew, -2.5, SEA_Y + 2, kit);
    // Sela's anchor at the lip: an iron ring on a stake, its wedge; the stake sinks as she drives it.
    const a = hexToWorld(L.anchor.q, L.anchor.r);
    const g = new THREE.Group();
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#4b5157', roughness: 0.5, metalness: 0.7 }));
    const stake = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.05, 0.07, 0.7, 6)), iron);
    stake.position.y = 0.3;
    g.add(stake);
    const ring = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.13, 0.03, 6, 14)), iron);
    ring.position.y = 0.66;
    g.add(ring);
    g.position.set(a.x * K + 0.45, 0, a.y * K + 0.35);
    this.group.add(g);
    this.anchor = g;
    // The rope coiled by her and over the lip.
    const rope = this.track(new THREE.MeshStandardMaterial({ color: ROPE, roughness: 0.9 }));
    const coil = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.22, 0.05, 5, 16).rotateX(Math.PI / 2)), rope);
    coil.position.set(a.x * K - 0.4, 0.05, a.y * K + 0.2);
    this.group.add(coil);
    this.buildPool();
    this.buildBats(false);
  }

  /** The shelves' dressing: loose stones and grit, a few tufts of salt grass, the shelves' front lips broken. */
  private dressShelves(kit: Kit, L: CliffLayout): void {
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    for (const l of L.ledges)
      for (const h of l.cells) {
        const p = hexToWorld(h.q, h.r);
        const k = hash(h.q * 31 + h.r * 17);
        const y = this.at(p.x, p.y).y;
        if (k < 0.28) kit.add(cap, ROCK[Math.floor(k * 13.9) % 4] as string, place(p.x * K + (k - 0.14) * 1.6, y + 0.06, p.y * K - (k - 0.14), k * 9, new THREE.Vector3(0.24, 0.12, 0.2)));
        else if (k > 0.86) kit.add(cap, '#6f7c6a', place(p.x * K + (k - 0.9) * 2, y + 0.04, p.y * K + 0.2, k * 5, new THREE.Vector3(0.22, 0.06, 0.18)));
      }
    cap.dispose();
  }

  /** The crows' nests (twigs and soot), and the deep cracks the shriekers live in (black, a violet sheen). */
  private buildNests(kit: Kit, L: CliffLayout): void {
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    L.nests.forEach((n, i) => {
      const w = hexToWorld(n.q, n.r);
      const p = this.at(w.x, w.y);
      this.nests.push({ p, k: i });
      // Soot smeared down the rock under it, the twigs in a cup, two pale bones.
      kit.add(cap, '#14161a', place(p.x, p.y + 0.02, p.z + 0.35, i, new THREE.Vector3(0.9, 0.08, 1.4)));
      for (let k = 0; k < 7; k++) kit.add(box, ['#3b3128', '#2f2720', '#463a2e'][k % 3] as string, place(p.x + Math.cos(k) * 0.28, p.y + 0.16 + (k % 2) * 0.05, p.z + Math.sin(k) * 0.2, k * 0.9, new THREE.Vector3(0.62, 0.05, 0.05)));
      kit.add(cap, '#0f1114', place(p.x, p.y + 0.12, p.z, 0, new THREE.Vector3(0.42, 0.14, 0.34)));
      kit.add(box, '#cfd3d4', place(p.x + 0.3, p.y + 0.2, p.z - 0.1, 0.7, new THREE.Vector3(0.22, 0.03, 0.03)));
    });
    L.holes.forEach((n, i) => {
      const w = hexToWorld(n.q, n.r);
      const p = this.at(w.x, w.y);
      this.holes.push({ p, k: i });
      // A deep split: black, jagged, its lips sharp; a cold sheen inside.
      for (let k = 0; k < 4; k++) kit.add(box, '#050608', place(p.x + (k - 1.5) * 0.28, p.y + 0.05, p.z + ((k % 2) - 0.5) * 0.14, 0.4 - k * 0.35, new THREE.Vector3(0.42, 0.1, 0.16)));
      for (const sd of [-1, 1]) kit.add(box, '#4a5058', place(p.x, p.y + 0.12, p.z + sd * 0.24, 0.1 * sd, new THREE.Vector3(1.3, 0.12, 0.08)));
      this.light(p.clone().setY(p.y + 0.2), '#7a6fb8', 1.2, 0.2);
    });
    cap.dispose();
    box.dispose();
  }

  /** Galo's crew: six red ropes down the wall to one side, a walker on each, going down (or a lantern at night). */
  private buildCrew(side: number, top: number, bottom: number, kit?: Kit): void {
    const red = this.track(new THREE.MeshStandardMaterial({ color: RED_ROPE, roughness: 0.8, emissive: '#3a0806' }));
    // A walker: a red cloak, a head, a lantern at night; one geometry in vertex colours, instanced for the six.
    const parts: [THREE.BufferGeometry, string][] = [
      [new THREE.CylinderGeometry(0.12, 0.18, 0.55, 6).translate(0, 0.3, 0), '#7d2a22'],
      [new THREE.SphereGeometry(0.1, 8, 6).translate(0, 0.66, 0), '#b98d74'],
    ];
    if (this.hour === 'night') parts.push([new THREE.SphereGeometry(0.06, 6, 5).translate(0.18, 0.35, -0.1), '#ffe2c2']);
    const walker = this.track(colourMerge(parts));
    for (const [g] of parts) g.dispose();
    const crewMesh = new THREE.InstancedMesh(walker, this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85 })), 6);
    crewMesh.frustumCulled = false;
    this.group.add(crewMesh);
    this.crewMesh = crewMesh;
    for (let i = 0; i < 6; i++) {
      const x = side * (11.6 + (i % 3) * 0.9 + (i > 2 ? 0.45 : 0));
      const zTop = -14 + (i % 2) * 0.4;
      const zBot = 14;
      const yTop = this.at(x / K, zTop / K).y + 0.4 + top * 0;
      const yBot = Math.max(bottom, this.at(x / K, zBot / K).y);
      const len = Math.hypot(zBot - zTop, yBot - yTop);
      const rope = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.025, 0.025, len, 4)), red);
      rope.position.set(x, (yTop + yBot) / 2 + 0.25, (zTop + zBot) / 2);
      rope.rotation.x = Math.atan2(zBot - zTop, yTop - yBot);
      // (In the merged dressing when there is one: six fewer draws.)
      if (kit) {
        rope.updateMatrix();
        kit.add(rope.geometry, RED_ROPE, rope.matrix);
      } else this.group.add(rope);
      this.crew.push({ x, top: zTop, bottom: zBot, k: i });
    }
  }

  /* ---------- acts 2 and 3: the wall ---------- */

  private buildWall(kit: Kit, s: BattleState, night: boolean): void {
    const L = wallLayout(s.seed, night);
    this.L = L;
    this.act = night ? 'night' : 'wall';
    this.shape = cliffShape(L, this.act);
    this.hour = night ? 'night' : 'dusk';
    this.buildSurface(night);
    this.dressShelves(kit, L);
    this.buildNests(kit, L);
    this.buildCrew(L.crew, 0, SEA_Y + 2, kit);
    // Aster's ledge (the goal): her old peg in the rock, a rag of her red cord on it; a faint warm glow (the cave).
    const goal = L.ledges.find((l) => l.goal);
    if (goal) {
      const mid = goal.cells[Math.floor(goal.cells.length / 2)] ?? goal.cells[0];
      if (mid) {
        const w = hexToWorld(mid.q, mid.r);
        const p = this.at(w.x, w.y);
        const box = new THREE.BoxGeometry(1, 1, 1);
        kit.add(box, '#1a1d21', place(p.x, p.y + 0.5, p.z - 1.0, 0, new THREE.Vector3(1.4, 1.1, 0.4)));
        kit.add(box, '#5a5f64', place(p.x + 0.4, p.y + 0.7, p.z - 0.8, 0.3, new THREE.Vector3(0.06, 0.06, 0.4)));
        kit.add(box, RED_ROPE, place(p.x + 0.45, p.y + 0.62, p.z - 0.62, 0.3, new THREE.Vector3(0.05, 0.22, 0.05)));
        box.dispose();
        this.light(new THREE.Vector3(p.x, p.y + 0.6, p.z - 0.7), WARM, 2.4, night ? 0.45 : 0.3, 1);
      }
    }
    // The cracks: the split in the merged dressing; the lips, pegs and arrows instanced (their look changes when sounded).
    for (const c of L.cracks) {
      const w = hexToWorld(c.h.q, c.h.r);
      const p = this.at(w.x, w.y);
      const k = hash(c.h.q * 9 + c.h.r * 5);
      this.crackLook(kit, p, k);
      this.cracks.push({ p, k, state: -2 });
    }
    this.buildCrackPools();
    // The hollow stones (the night): pale, holed, on the back of each ledge (one instanced mesh).
    if (L.stones.length) {
      const stoneMat = this.track(new THREE.MeshStandardMaterial({ color: '#c9ccc8', roughness: 0.7, flatShading: true }));
      const sm = new THREE.InstancedMesh(this.track(new THREE.DodecahedronGeometry(0.3, 0).scale(1, 1.3, 0.9)), stoneMat, L.stones.length);
      L.stones.forEach((st, i) => {
        const w = hexToWorld(st.h.q, st.h.r);
        const p = this.at(w.x, w.y);
        this.m4.makeTranslation(p.x, p.y + 0.32, p.z);
        sm.setMatrixAt(i, this.m4);
        kit.add(new THREE.CircleGeometry(0.08, 8), '#111316', place(p.x, p.y + 0.4, p.z + 0.272));
        this.stones.push({ p });
      });
      sm.castShadow = true;
      this.group.add(sm);
      this.stoneMesh = sm;
    }
    // The ropes: pale and thick enough to read on the dark rock from a phone; a faint glow of their own at night.
    this.ropeMat = this.track(new THREE.MeshStandardMaterial({ color: '#d3d9dd', roughness: 0.75, emissive: night ? '#2a3038' : '#16191c' }));
    // The rope the hero goes down on (one stretched cylinder, moved each frame).
    const trip = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.05, 0.05, 1, 6).translate(0, 0.5, 0)), this.ropeMat);
    trip.visible = false;
    this.group.add(trip);
    this.tripRope = trip;
    // The rope's reach: a red arc on the rock round the hero.
    const arcG = this.track(new THREE.BufferGeometry());
    arcG.setAttribute('position', new THREE.BufferAttribute(new Float32Array(97 * 3), 3));
    const arc = new THREE.Line(arcG, this.track(new THREE.LineBasicMaterial({ color: '#ff6a52', transparent: true, opacity: 0.85, depthTest: false })));
    arc.renderOrder = 7;
    arc.frustumCulled = false;
    this.group.add(arc);
    this.arc = arc;
    this.buildPool();
    this.buildBats(night);
  }

  /** A crack's split: jagged black, in the merged dressing (one draw for all of them); its lips are instanced. */
  private crackLook(kit: Kit, p: THREE.Vector3, k: number): void {
    const box = new THREE.BoxGeometry(0.34, 0.1, 0.12);
    for (let i = 0; i < 4; i++) kit.add(box, '#07080a', place(p.x + (i - 1.5) * 0.24, p.y + 0.06, p.z + ((i % 2) - 0.5) * 0.12, 0.5 - i * 0.4 + k * 0.3));
    box.dispose();
  }

  /**
   * The cracks' lips (a raised chalky ring round each split, recoloured by what it sounded like), the iron pegs of the
   * firm ones and the arrows fallen under the hollow ones: three instanced meshes for all of them.
   */
  private buildCrackPools(): void {
    const n = this.cracks.length;
    if (!n) return;
    const lipMat = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.8, flatShading: true }));
    const lips = new THREE.InstancedMesh(this.track(new THREE.TorusGeometry(0.46, 0.09, 4, 10).rotateX(Math.PI / 2).scale(1.2, 1, 0.6)), lipMat, n);
    this.cracks.forEach((c, i) => {
      this.m4.makeTranslation(c.p.x, c.p.y + 0.06, c.p.z);
      lips.setMatrixAt(i, this.m4);
      lips.setColorAt(i, this.c3.set('#aeb8c0'));
    });
    this.group.add(lips);
    this.lips = lips;
    const peg = new THREE.InstancedMesh(this.track(new THREE.CylinderGeometry(0.05, 0.035, 0.45, 6)), this.track(new THREE.MeshStandardMaterial({ color: '#9aa2a9', metalness: 0.7, roughness: 0.35 })), n);
    const arrow = new THREE.InstancedMesh(this.track(new THREE.BoxGeometry(0.035, 0.035, 0.5)), this.track(new THREE.MeshStandardMaterial({ color: '#5b4632' })), n);
    for (const m of [peg, arrow]) {
      m.count = 0;
      this.group.add(m);
    }
    this.pegs = peg;
    this.arrows = arrow;
  }

  /** The sounds' rings: an instanced pool of flat bands with dark edges (they read on dark rock and on foam). */
  private buildPool(): void {
    const bands: [number, number, number][] = [
      [0.94, 0.958, 0.1],
      [0.958, 0.99, 1],
      [0.99, 1.004, 0.1],
    ];
    const pos: number[] = [];
    const col: number[] = [];
    const idx: number[] = [];
    const seg = 56;
    for (const [a, b, k] of bands) {
      const base = pos.length / 3;
      for (let i = 0; i <= seg; i++) {
        const t = (i / seg) * Math.PI * 2;
        pos.push(Math.cos(t) * a, 0, Math.sin(t) * a, Math.cos(t) * b, 0, Math.sin(t) * b);
        col.push(k, k, k, k, k, k);
      }
      for (let i = 0; i < seg; i++) {
        const v = base + i * 2;
        idx.push(v, v + 2, v + 1, v + 1, v + 2, v + 3);
      }
    }
    const g = this.track(new THREE.BufferGeometry());
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    g.setIndex(idx);
    const m = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', vertexColors: true, transparent: true, opacity: 0.95, depthWrite: false, depthTest: false }));
    const pool = new THREE.InstancedMesh(g, m, 32);
    pool.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(32 * 3), 3);
    pool.count = 0;
    pool.renderOrder = 8;
    pool.frustumCulled = false;
    pool.name = 'galo-sound';
    this.ringPool = pool;
    this.group.add(pool);
  }

  /**
   * The shriekers: small, blind, leather-winged. A body (a pale-pink grey, ears up, a red mouth), two wings that beat:
   * three instanced meshes for the flock. Their mother: the same, three times as big, her wings torn, built once.
   */
  private buildBats(night: boolean): void {
    const skin = this.track(new THREE.MeshStandardMaterial({ color: '#7a6466', roughness: 0.75, emissive: night ? '#1a0e10' : '#000000' }));
    const wing = this.track(new THREE.MeshStandardMaterial({ color: '#4a3a3e', roughness: 0.8, side: THREE.DoubleSide, emissive: night ? '#120a0c' : '#000000' }));
    const bodyG = this.track(new THREE.SphereGeometry(0.16, 8, 6).scale(1, 0.9, 1.3));
    // A wing: a leather sail with its fingers' scallops (a fan of triangles), from the body out along +x.
    const wingG = this.track(new THREE.BufferGeometry());
    const wp: number[] = [0, 0, 0.05, 0.55, 0.06, 0.18, 0.42, 0.02, -0.08, 0, 0, -0.08, 0.42, 0.02, -0.08, 0.3, 0, -0.22, 0, 0, 0.05, 0.42, 0.02, -0.08, 0.55, 0.06, 0.18];
    wingG.setAttribute('position', new THREE.Float32BufferAttribute(wp, 3));
    wingG.computeVertexNormals();
    const wingL = this.track(wingG.clone().scale(-1, 1, 1));
    const n = 14;
    const body = new THREE.InstancedMesh(bodyG, skin, n);
    const wl = new THREE.InstancedMesh(wingL, wing, n);
    const wr = new THREE.InstancedMesh(wingG, wing, n);
    for (const m of [body, wl, wr]) {
      m.count = 0;
      m.frustumCulled = false;
      this.group.add(m);
    }
    this.bats = { body, wl, wr };
    if (this.act !== 'night') return;
    // The mother.
    const g = new THREE.Group();
    const b = new THREE.Mesh(bodyG, skin);
    b.scale.setScalar(3);
    g.add(b);
    const ears = this.track(new THREE.ConeGeometry(0.06, 0.22, 4));
    for (const sd of [-1, 1]) {
      const e = new THREE.Mesh(ears, skin);
      e.position.set(sd * 0.22, 0.48, 0.12);
      e.rotation.z = -sd * 0.3;
      e.scale.setScalar(1.6);
      g.add(e);
    }
    const mouth = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.09, 8, 6)), this.track(new THREE.MeshBasicMaterial({ color: '#a0211a' })));
    mouth.position.set(0, -0.02, 0.48);
    mouth.scale.set(1.6, 0.8, 0.6);
    g.add(mouth);
    const mk = (geo: THREE.BufferGeometry): THREE.Object3D => {
      const o = new THREE.Mesh(geo, wing);
      o.scale.setScalar(3.4);
      g.add(o);
      return o;
    };
    const wl2 = mk(wingL);
    const wr2 = mk(wingG);
    g.visible = false;
    this.group.add(g);
    this.mother = { g, wl: wl2, wr: wr2 };
  }

  /* ---------- the scenes ---------- */

  private buildScene(kit: Kit, place0: string): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    if (place0 === 'flankSea') {
      // The wall from the sea at dusk: the wall's own layout (a fixed one), the cut rope, the six red ones.
      const L = wallLayout(7, false);
      this.L = L;
      this.act = 'wall';
      this.shape = cliffShape(L, 'wall');
      this.hour = 'dusk';
      this.buildSurface(false);
      this.buildNests(kit, L);
      this.buildCrew(L.crew, 0, SEA_Y + 2);
      // The old rope hanging from a crack, cut: grey, frayed at its end.
      const c = L.cracks[0];
      if (c) {
        const w = hexToWorld(c.h.q, c.h.r);
        const p = this.at(w.x, w.y);
        kit.add(box, '#7d8286', place(p.x, p.y - 1.6, p.z + 0.9, 0, new THREE.Vector3(0.05, 3.2, 0.05), 0.55));
        kit.add(cap, '#c9ccc8', place(p.x, p.y - 3.0, p.z + 1.75, 0, 0.08));
      }
    } else if (place0 === 'asterLedge') {
      // Aster's ledge: a small cave; its walls of the flank's black rock, a ring of stones round the fire, the page
      // stuck with resin on the rock, her old cord; the night over the sea at its mouth (south).
      this.hour = 'night';
      for (let i = 0; i < 22; i++) {
        const a = Math.PI + (i / 21) * Math.PI;
        const r = 3.6 + hash(i) * 0.5;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r * 0.9;
        kit.add(cap, ROCK[i % 4] as string, place(x, 1.2, z, i, new THREE.Vector3(1.6, 2.8 + hash(i * 3) * 1.2, 1.4)));
      }
      kit.add(box, '#23272c', place(0, 3.2, -1.4, 0, new THREE.Vector3(8, 0.6, 5)));
      kit.add(new THREE.CylinderGeometry(4.4, 4.6, 0.2, 10), '#4c5257', place(0, -0.1, 0, 0));
      for (let k = 0; k < 10; k++) {
        const a = (k / 10) * Math.PI * 2;
        kit.add(cap, '#6a7076', place(Math.cos(a) * 0.62, 0.08, Math.sin(a) * 0.62, a, new THREE.Vector3(0.24, 0.16, 0.2)));
      }
      // The page: pale paper on the rock, its lines; the resin's amber smear round it.
      kit.add(box, '#7a5a2a', place(-1.6, 1.3, -2.85, 0.15, new THREE.Vector3(0.66, 0.84, 0.02)));
      kit.add(box, '#e4e6e2', place(-1.6, 1.3, -2.83, 0.15, new THREE.Vector3(0.52, 0.7, 0.02)));
      for (let k = 0; k < 6; k++) kit.add(box, '#41454a', place(-1.6, 1.52 - k * 0.09, -2.815, 0.15, new THREE.Vector3(0.38, 0.012, 0.01)));
      kit.add(new THREE.TorusGeometry(0.22, 0.04, 5, 14), RED_ROPE, place(1.5, 0.05, -1.6, 0, 1, Math.PI / 2, 0));
      this.light(new THREE.Vector3(0, 0.6, 0), WARM, 4.2, 0.6, 1);
    } else if (place0 === 'cliffGrab') {
      // The last ledge at night: rock, the anchor jumped out (its hole, the stake on the ground), a red rope from above.
      this.hour = 'night';
      const L = wallLayout(7, true);
      this.L = L;
      this.act = 'night';
      this.shape = { act: 'night', shelves: [{ level: 0, x0: -9000, x1: 9000, y0: -1800, y1: 1600, h: 0 }] };
      this.buildSurface(true);
      kit.add(box, '#4b5157', place(0.2, 0.06, 0.5, 0.9, new THREE.Vector3(0.6, 0.08, 0.08)));
      kit.add(cap, '#07080a', place(-0.1, 0.02, 0.7, 0, new THREE.Vector3(0.3, 0.04, 0.24)));
      kit.add(box, RED_ROPE, place(1.2, 3.2, -1.0, 0, new THREE.Vector3(0.04, 6.4, 0.04)));
      this.light(new THREE.Vector3(1.3, 1.4, -0.9), '#ffd2a0', 2.2, 0.55, 1);
    } else {
      // The foot of the cliff: a beach of black pebbles, the fire, the wall up behind (north), the sea (south).
      this.hour = 'night';
      this.shape = { act: 'edge', shelves: [{ level: 0, x0: -16000, x1: 16000, y0: -3000, y1: 3500, h: 0 }] };
      this.act = 'edge';
      this.buildSurface(true);
      for (let i = 0; i < 60; i++) {
        const x = (hash(i * 3) - 0.5) * 12;
        const z = (hash(i * 7) - 0.5) * 6;
        kit.add(cap, ['#24272b', '#2c3035', '#1d2023'][i % 3] as string, place(x, 0.03, z, i, new THREE.Vector3(0.22, 0.08, 0.18)));
      }
      for (let i = 0; i < 6; i++) kit.add(box, RED_ROPE, place(-3 + i * 1.1, 6, -4.6, 0, new THREE.Vector3(0.04, 12, 0.04)));
      this.light(new THREE.Vector3(1.2, 0.5, 0), WARM, 3.0, 0.5, 1);
    }
    box.dispose();
    cap.dispose();
  }

  /* ---------- the board ---------- */

  /** Whether the sim's enemy is drawn here (the shriekers, their mother). */
  owns(id: number, s: BattleState | null): boolean {
    const w = (s ?? this.lastState)?.senda?.cliff;
    return !!w && (w.shriekers.includes(id) || (w.mother.id > 0 && w.mother.id === id));
  }

  /** Its bar's width (0: none: the shriekers are too many for bars). */
  barOf(id: number): number {
    const w = this.lastState?.senda?.cliff;
    return w && w.mother.id === id ? 2.0 : 0;
  }

  /** The board's ground made the wall: its heights rewritten, its own mesh hidden (the wall is drawn here). */
  apply(terrain: TerrainBuild): void {
    const sh = this.shape;
    if (sh) {
      const R = this.map.radius;
      const W = R * 2 + 1;
      for (let id = 0; id < terrain.heights.length; id++) {
        const q = (id % W) - R;
        const r = Math.floor(id / W) - R;
        const p = hexToWorld(q, r);
        terrain.heights[id] = cliffH(sh, p.x, p.y);
      }
      (terrain as { ground?: (x: number, z: number) => number }).ground = (x: number, z: number) => cliffH(sh, x / K, z / K);
      terrain.mesh.visible = false;
      terrain.decor.visible = false;
      if (terrain.fog) terrain.fog.group.visible = false;
    }
    const room = this.place === 'asterLedge';
    if (room) {
      terrain.mesh.visible = false;
      terrain.decor.visible = false;
      if (terrain.fog) terrain.fog.group.visible = false;
    }
    if (!room) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(this.hour);
  }

  paint(hour0: string): void {
    const hour = (this.place === 'cliffTop' ? 'day' : this.place === 'cliffWall' || this.place === 'flankSea' ? 'dusk' : hour0 in HOURS ? hour0 : this.hour) as Hour;
    const look = HOURS[this.place === 'cliffNight' || this.place === 'asterLedge' || this.place === 'cliffGrab' || this.place === 'cliffFoot' ? 'night' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
      key.position.set(look.sun[0] * 40, Math.max(8, look.sun[1] * 60), look.sun[2] * 40 + 10);
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(look.sun[0], look.sun[1], look.sun[2]);
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    const t = this.now;
    const r = s.rules?.senda?.cliff;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.at(e.x, e.y, 0.25);
      if (e.kind === 'cliffFirm') {
        // Deep: a big slow ring, pale blue; the peg and the rope.
        this.ring(p, 0.3, 4.2, t, 1.6, DEEP, 0.9);
        this.ring(p, 0.2, 2.4, t + 0.25, 1.2, DEEP, 0.6);
        this.ctx.vfx.burst(p, { n: 10, color: '#c9d6e2', speed: 1.2, up: 1.0, size: 0.08, life: 12 });
      } else if (e.kind === 'cliffHollow') {
        // High: small quick rings, chips of pale stone, the arrow falling.
        for (let k = 0; k < 3; k++) this.ring(p, 0.1, 1.4, t + k * 0.12, 0.35, PALE, 0.95);
        this.ctx.vfx.burst(p, { n: 14, color: '#d5d9dc', speed: 2.2, up: 1.6, size: 0.07, life: 14, gravity: 1 });
      } else if (e.kind === 'cliffCrumb' || e.kind === 'cliffHang') {
        this.ring(p, 0.2, 2.4, t, 0.9, RED_SOFT, 0.85);
        this.ctx.vfx.burst(p, { n: 18, color: '#8d8478', speed: 0.8, up: -0.5, size: 0.1, life: 30, smoke: true, gravity: 0.6 });
      } else if (e.kind === 'cliffFall') {
        this.ring(p, 0.2, 3.0, t, 0.6, RED, 1);
        this.ctx.vfx.burst(p, { n: 26, color: '#7a7064', speed: 2.4, up: -1, size: 0.14, life: 30, gravity: 2 });
        this.ctx.shake(0.35);
      } else if (e.kind === 'cliffStone') {
        // The hollow stone rings: red rings out of it while it sounds (the frames add the rest).
        this.ring(p, 0.2, 5.4, t, 1.2, RED, 0.95);
      } else if (e.kind === 'cliffHammer') {
        this.ring(p, 0.2, 3.2, t, 0.8, PALE, 0.8);
        this.ctx.vfx.burst(this.at(e.x, e.y, 0.5), { n: 6, color: '#ffd2a0', speed: 1.6, up: 1.2, size: 0.05, life: 8 });
      } else if (e.kind === 'cliffNest') {
        this.ctx.vfx.burst(p, { n: 22, color: '#14161a', speed: 1.4, up: 1.4, size: 0.18, life: 26, smoke: true });
      } else if (e.kind === 'shriekCall') {
        this.ring(p, 0.2, 2.2, t, 0.7, RED_SOFT, 0.8);
      } else if (e.kind === 'shriekWarn') {
        this.motherRing = { t0: t, x: p.x, z: p.z, y: p.y };
      } else if (e.kind === 'shriek') {
        this.motherRing = null;
        const R = (r?.night?.mother.r ?? 5000) * K;
        this.ring(p, R * 0.95, R * 1.15, t, 0.5, PALE, 1);
        this.ctx.shake(0.4);
      } else if (e.kind === 'cliffLand') {
        this.ring(p, 0.2, 1.6, t, 0.6, PALE, 0.7);
      }
    }
  }

  private ring(p: THREE.Vector3, r0: number, r1: number, t0: number, life: number, color: THREE.Color, alpha: number): void {
    this.rings.push({ x: p.x, y: p.y, z: p.z, r0, r1, t0, life, color, alpha });
  }

  draw(s: BattleState): void {
    this.lastState = s;
    const r = s.rules?.senda?.cliff;
    const w = s.senda?.cliff;
    if (!r || !w || !this.L) return;
    // The cracks as they sounded (the pools rewritten only when one changes).
    let changed = false;
    r.cracks.forEach((c, i) => {
      const v = this.cracks[i];
      if (!v) return;
      const st = w.sounded[i] ? c.kind : -1;
      if (st === v.state) return;
      v.state = st;
      changed = true;
      // Untried: chalky grey; firm: cold blue; hollow: chipped white; crumbling: rust.
      this.lips?.setColorAt(i, this.c3.set(st === 0 ? '#8fb4dc' : st === 1 ? '#e6eaec' : st === 2 ? '#c4825e' : '#aeb8c0'));
    });
    if (changed && this.lips && this.pegs && this.arrows) {
      if (this.lips.instanceColor) this.lips.instanceColor.needsUpdate = true;
      let np = 0;
      let na = 0;
      for (const v of this.cracks) {
        if (v.state === 0) {
          // The peg, driven in at a slant.
          this.m4.compose(this.v3.set(v.p.x, v.p.y + 0.22, v.p.z + 0.1), this.q4.setFromEuler(this.e3.set(0.9, 0, 0)), this.s3.set(1, 1, 1));
          this.pegs.setMatrixAt(np++, this.m4);
        } else if (v.state === 1) {
          // The arrow fallen out: a short shaft lying under it.
          this.m4.compose(this.v3.set(v.p.x + 0.2, v.p.y - 0.04, v.p.z + 0.5), this.q4.setFromEuler(this.e3.set(0, 0.6, 0)), this.s3.set(1, 1, 1));
          this.arrows.setMatrixAt(na++, this.m4);
        }
      }
      this.pegs.count = np;
      this.arrows.count = na;
      this.pegs.instanceMatrix.needsUpdate = true;
      this.arrows.instanceMatrix.needsUpdate = true;
    }
    // The ropes left: one tube mesh, rebuilt when one is added.
    const key = w.ropes.map((x) => `${x.crack}`).join(',');
    if (key !== this.ropeKey) {
      this.ropeKey = key;
      if (this.ropeMesh) {
        this.ropeMesh.geometry.dispose();
        this.group.remove(this.ropeMesh);
        this.ropeMesh = null;
      }
      const parts: THREE.BufferGeometry[] = [];
      for (const rp of w.ropes) {
        const c = r.cracks[rp.crack];
        if (!c) continue;
        const a = this.at(c.x, c.y, 0.2);
        const l = r.ledges[c.land];
        const foot = l ? this.footOf(s, r, rp.crack) : a.clone().setY(a.y - 3);
        const mid = a.clone().lerp(foot, 0.5);
        mid.z += 0.35;
        mid.y -= 0.2;
        parts.push(new THREE.TubeGeometry(new THREE.QuadraticBezierCurve3(a, mid, foot), 16, 0.055, 5, false));
      }
      if (parts.length && this.ropeMat) {
        const g = mergeGeos(parts);
        for (const p of parts) p.dispose();
        this.ropeMesh = new THREE.Mesh(g, this.ropeMat);
        this.group.add(this.ropeMesh);
      }
    }
  }

  private footOf(s: BattleState, r: CliffRules, crack: number): THREE.Vector3 {
    const c = r.cracks[crack] as CliffRules['cracks'][number];
    const l = r.ledges[c.land];
    if (!l) return this.at(c.x, c.y);
    let best = this.at(c.x, c.y);
    let bd = Infinity;
    const R = s.map.radius;
    const W = R * 2 + 1;
    for (const id of l.cells) {
      const q = (id % W) - R;
      const rr = Math.floor(id / W) - R;
      const p = hexToWorld(q, rr);
      const d = Math.abs(p.x - c.x) * 2 + Math.abs(p.y - c.y);
      if (d < bd) {
        bd = d;
        best = this.at(p.x, p.y, 0.1);
      }
    }
    return best;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    const s = this.lastState;
    const r = s?.rules?.senda?.cliff;
    const w = s?.senda?.cliff;
    this.glow.begin();
    // Galo's crew going down their ropes, slowly (a walker every so far; at night each with a lantern).
    if (this.crewMesh && this.shape) {
      for (const c of this.crew) {
        const k = (now * 0.012 + c.k * 0.13) % 1;
        const z = c.top + (c.bottom - c.top) * k;
        const y = cliffH(this.shape, c.x / K, z / K) + 0.2;
        this.m4.makeTranslation(c.x + 0.25, y, z + 0.3);
        this.crewMesh.setMatrixAt(c.k, this.m4);
        if (this.hour === 'night') this.glow.add(c.x + 0.4, y + 0.4, z + 0.2, WARM, 1.6, 0.7);
      }
      this.crewMesh.instanceMatrix.needsUpdate = true;
    }
    // The anchor sinks as Sela drives it (the cornice: the act's clock).
    if (this.anchor && s && r?.edge) {
      const k = Math.min(1, s.waveT / r.edge.time);
      this.anchor.position.y = -0.42 * k;
    }
    if (s && r && w) {
      const h = s.heroes[0];
      const hp = h ? (this.ctx.pos(h.id) ?? this.at(h.x, h.y)) : null;
      // The rope's reach round the hero (on a ledge or hanging), the cracks in it lit; none while on a rope.
      if (this.arc && hp) {
        const show = !w.trip && r.act !== 'edge';
        this.arc.visible = show;
        if (show && hp.distanceToSquared(this.arcAt) > 0.04) {
          this.arcAt.copy(hp);
          const pos = this.arc.geometry.getAttribute('position') as THREE.BufferAttribute;
          const R = r.rope;
          const hx = h ? h.x : 0;
          const hy = h ? h.y : 0;
          for (let i = 0; i <= 96; i++) {
            const a = (i / 96) * Math.PI * 2;
            const x = hx + Math.cos(a) * R;
            const y = hy + Math.sin(a) * R;
            pos.setXYZ(i, x * K, this.at(x, y).y + 0.18, y * K);
          }
          pos.needsUpdate = true;
        }
        if (show && h)
          r.cracks.forEach((c, i) => {
            const v = this.cracks[i];
            if (!v) return;
            const near = (c.x - h.x) * (c.x - h.x) + (c.y - h.y) * (c.y - h.y) <= r.rope * r.rope;
            if (near && !(w.sounded[i] && c.kind === 1)) this.glow.add(v.p.x, v.p.y + 0.3, v.p.z, w.sounded[i] && c.kind === 0 ? DEEP : RED, 1.2, 0.35 + 0.15 * Math.sin(now * 4 + i));
          });
      }
      // The rope the hero is on: from the crack to the hero.
      if (this.tripRope) {
        const t = w.trip;
        const ci = w.hang >= 0 ? w.hang : t ? this.tripCrack(r, t.pts) : -1;
        const c = ci >= 0 ? r.cracks[ci] : undefined;
        if (c && hp) {
          const a = this.at(c.x, c.y, 0.2);
          const b = hp.clone().setY(hp.y + 0.7);
          const d = b.clone().sub(a);
          this.tripRope.visible = true;
          this.tripRope.position.copy(a);
          this.tripRope.scale.set(1, d.length(), 1);
          this.tripRope.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
        } else this.tripRope.visible = false;
      }
      // Hanging from a crumbling crack: its dust, its count.
      if (w.hang >= 0) {
        const v = this.cracks[w.hang];
        const left = s ? Math.max(0, (w.gives - s.tick) / 12) : 0;
        if (v) {
          if (Math.random() < 0.3) this.ctx.vfx.burst(v.p.clone().setY(v.p.y + 0.1), { n: 2, color: '#8d8478', speed: 0.4, up: -0.8, size: 0.07, life: 22, gravity: 0.8 });
          this.glow.add(v.p.x, v.p.y + 0.4, v.p.z, left < 3 ? RED : RED_SOFT, 1.4, 0.4 + 0.3 * Math.sin(now * (left < 3 ? 12 : 5)));
        }
      }
      // A hollow stone ringing: red rings out of it, one every half second.
      if (w.stone.i >= 0 && s.tick < w.stone.until) {
        const st = this.stones[w.stone.i];
        if (st) {
          if (Math.floor(now * 2) !== Math.floor((now - dt) * 2)) this.ring(st.p.clone().setY(st.p.y + 0.3), 0.2, 4.4, now, 1.0, RED, 0.85);
          this.glow.add(st.p.x, st.p.y + 0.5, st.p.z, RED, 1.6, 0.6);
          if (this.stoneMesh) {
            this.m4.makeTranslation(st.p.x + Math.sin(now * 40) * 0.02, st.p.y + 0.32, st.p.z);
            this.stoneMesh.setMatrixAt(w.stone.i, this.m4);
            this.stoneMesh.instanceMatrix.needsUpdate = true;
            this.stoneShook = true;
          }
        }
      } else if (this.stoneShook && this.stoneMesh) {
        this.stoneShook = false;
        this.stones.forEach((st, i) => {
          this.m4.makeTranslation(st.p.x, st.p.y + 0.32, st.p.z);
          this.stoneMesh?.setMatrixAt(i, this.m4);
        });
        this.stoneMesh.instanceMatrix.needsUpdate = true;
      }
      this.drawBats(s, now);
    }
    // The rings.
    const pool = this.ringPool;
    let n = 0;
    const live: RingFx[] = [];
    for (const g of this.rings) {
      const k = (now - g.t0) / g.life;
      if (k > 1) continue;
      live.push(g);
      if (k < 0 || !pool || n >= 32) continue;
      const rad = g.r0 + (g.r1 - g.r0) * k;
      this.m4.compose(this.v3.set(g.x, g.y, g.z), this.q4.identity(), this.s3.set(rad, 1, rad));
      pool.setMatrixAt(n, this.m4);
      pool.setColorAt(n, this.c3.copy(g.color).multiplyScalar(g.alpha * (1 - k * 0.75)));
      n++;
    }
    // The mother's ring, growing for its warning.
    if (this.motherRing && pool && n < 32) {
      const W = r?.night?.mother;
      const k = W ? Math.min(1, (now - this.motherRing.t0) / (W.warn / 12)) : 1;
      const R = (W?.r ?? 5000) * K;
      this.m4.compose(this.v3.set(this.motherRing.x, this.motherRing.y, this.motherRing.z), this.q4.identity(), this.s3.set(R * k, 1, R * k));
      pool.setMatrixAt(n, this.m4);
      pool.setColorAt(n, this.c3.copy(RED).multiplyScalar(0.6 + 0.4 * Math.sin(now * 14)));
      n++;
      if (k >= 1) this.motherRing = null;
    }
    this.rings = live;
    if (pool && (n || pool.count)) {
      pool.count = n;
      pool.instanceMatrix.needsUpdate = true;
      if (pool.instanceColor) pool.instanceColor.needsUpdate = true;
    }
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * 6 + l.p.x) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    this.glow.end();
  }

  /** The crack a trip goes by (its second point), if it is one. */
  private tripCrack(r: CliffRules, pts: { x: number; y: number }[]): number {
    for (const p of pts.slice(1)) {
      const i = r.cracks.findIndex((c) => Math.abs(c.x - p.x) < 10 && Math.abs(c.y - p.y) < 260);
      if (i >= 0) return i;
    }
    return -1;
  }

  /** The shriekers and their mother where the board has them, wings beating; their eyeless faces to the sound. */
  private drawBats(s: BattleState, now: number): void {
    const w = s.senda?.cliff;
    const b = this.bats;
    if (!w || !b) return;
    let n = 0;
    for (const id of w.shriekers) {
      if (n >= 14) break;
      const e = s.enemies.find((x) => x.id === id);
      if (!e || e.hp <= 0) continue;
      const p = this.ctx.pos(id) ?? this.at(e.x, e.y);
      const y = p.y + 1.0 + Math.sin(now * 7 + id) * 0.12;
      const yaw = Math.PI / 2 - (e.facing / 1024) * Math.PI * 2;
      const beat = Math.sin(now * 22 + id * 1.3) * 0.7;
      this.q4.setFromEuler(this.e3.set(0, yaw, 0));
      this.m4.compose(this.v3.set(p.x, y, p.z), this.q4, this.s3.set(1, 1, 1));
      b.body.setMatrixAt(n, this.m4);
      this.q4.setFromEuler(this.e3.set(0, yaw, beat, 'YXZ'));
      this.m4.compose(this.v3.set(p.x, y, p.z), this.q4, this.s3.set(1, 1, 1));
      b.wr.setMatrixAt(n, this.m4);
      this.q4.setFromEuler(this.e3.set(0, yaw, -beat, 'YXZ'));
      this.m4.compose(this.v3.set(p.x, y, p.z), this.q4, this.s3.set(1, 1, 1));
      b.wl.setMatrixAt(n, this.m4);
      // A faint red mouth glow (they have no eyes: what shows is the mouth).
      this.glow.add(p.x, y, p.z, EMBER, 0.5, 0.55);
      n++;
    }
    // (Uploaded only while there are some, or the frame they all went: a buffer a frame each was half the cost.)
    if (n || b.body.count)
      for (const m of [b.body, b.wl, b.wr]) {
        m.count = n;
        m.instanceMatrix.needsUpdate = true;
      }
    const M = this.mother;
    if (M) {
      const e = w.mother.id > 0 ? s.enemies.find((x) => x.id === w.mother.id && x.hp > 0) : undefined;
      M.g.visible = !!e;
      if (e) {
        const p = this.ctx.pos(e.id) ?? this.at(e.x, e.y);
        const y = p.y + 2.2 + Math.sin(now * 2.2) * 0.2;
        M.g.position.set(p.x, y, p.z);
        M.g.rotation.y = Math.PI / 2 - (e.facing / 1024) * Math.PI * 2;
        const beat = Math.sin(now * 9) * 0.6 + (w.mother.ring >= 0 ? 0.6 : 0);
        M.wr.rotation.z = beat;
        M.wl.rotation.z = -beat;
        this.glow.add(p.x, y, p.z, w.mother.ring >= 0 ? RED : EMBER, w.mother.ring >= 0 ? 3.6 : 1.6, 0.6);
      }
    }
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.ropeMesh?.geometry.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** Merges geometries painted one colour each (position, normal, color) into one. */
const colourMerge = (parts: [THREE.BufferGeometry, string][]): THREE.BufferGeometry => {
  const pos: number[] = [];
  const nor: number[] = [];
  const col: number[] = [];
  const c = new THREE.Color();
  for (const [p, hex] of parts) {
    const g = p.index ? p.toNonIndexed() : p;
    const a = g.getAttribute('position') as THREE.BufferAttribute;
    const b = g.getAttribute('normal') as THREE.BufferAttribute;
    c.set(hex);
    for (let i = 0; i < a.count; i++) {
      pos.push(a.getX(i), a.getY(i), a.getZ(i));
      nor.push(b.getX(i), b.getY(i), b.getZ(i));
      col.push(c.r, c.g, c.b);
    }
    if (g !== p) g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  out.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return out;
};

/** Merges tube geometries (position, normal) into one. */
const mergeGeos = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry => {
  const pos: number[] = [];
  const nor: number[] = [];
  for (const p of parts) {
    const g = p.index ? p.toNonIndexed() : p;
    const a = g.getAttribute('position') as THREE.BufferAttribute;
    const b = g.getAttribute('normal') as THREE.BufferAttribute;
    for (let i = 0; i < a.count; i++) {
      pos.push(a.getX(i), a.getY(i), a.getZ(i));
      nor.push(b.getX(i), b.getY(i), b.getZ(i));
    }
    if (g !== p) g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  return out;
};
